// 先进先出通知队列（上限 20 条），4 秒后自动淘汰，支持按 key 去重。
// 设计依据：design.md §4.2 / tasks.md T-3.2.5。
import { create } from 'zustand';

export interface NotificationPayload {
  message: string;
  level?: 'info' | 'success' | 'warning' | 'danger';
  key?: string;
  ttlMs?: number;
}

export interface Notification extends NotificationPayload {
  id: string;
  createdAt: number;
}

export interface NotificationState {
  queue: Notification[];
  push: (n: NotificationPayload) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const CAP = 20;
const DEFAULT_TTL = 4_000;

let _seq = 1;
let _timers = new Map<string, ReturnType<typeof setTimeout>>();

// 记录每个 key 最近一次被接受的时间戳，用来去掉短时间内的重复通知，
// 避免调用方刷屏。500ms 在体验上可视为“同一瞬间”。
const DEDUPE_WINDOW_MS = 500;
let _lastByKey = new Map<string, number>();

export const useNotificationStore = create<NotificationState>((set, get) => ({
  queue: [],

  push(n) {
    const now = Date.now();
    if (n.key) {
      const last = _lastByKey.get(n.key) ?? 0;
      if (now - last < DEDUPE_WINDOW_MS) {
        return n.key;
      }
      _lastByKey.set(n.key, now);
    }

    const id = `n-${_seq++}`;
    const notif: Notification = {
      ...n,
      id,
      createdAt: now,
    };
    set((state) => {
      const next = [...state.queue, notif];
      if (next.length > CAP) next.splice(0, next.length - CAP);
      return { queue: next };
    });

    const ttl = n.ttlMs ?? DEFAULT_TTL;
    const timer = setTimeout(() => {
      get().dismiss(id);
    }, ttl);
    _timers.set(id, timer);
    return id;
  },

  dismiss(id) {
    const t = _timers.get(id);
    if (t) {
      clearTimeout(t);
      _timers.delete(id);
    }
    set((state) => ({ queue: state.queue.filter((n) => n.id !== id) }));
  },

  clear() {
    for (const t of _timers.values()) clearTimeout(t);
    _timers = new Map();
    _lastByKey = new Map();
    set({ queue: [] });
  },
}));

export function pushNotification(n: NotificationPayload): string {
  return useNotificationStore.getState().push(n);
}
